/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import dayjs from "dayjs";
import {
  ALL_FILTER_VALUE,
  PERQ_REPORT_DATE_FILTERS,
  resolveDateRange,
} from "../../../utils/perqReportDrilldown";
import { isValidDayjsValue } from "../../../utils/isValidDayjsValue";
import { isValidCustomDateRange } from "../../../utils/isValidCustomDateRange";

export const DATE_FILTERS = PERQ_REPORT_DATE_FILTERS;

// "all" is the unselected state (no has_ot_discount sent), so it isn't offered as an option.
export const ALL_ORDER_TYPE = "all";
export const ORDER_TYPES = ["ot", "non_ot"];
export const ORDER_TYPE_PLACEHOLDER = "All orders";

export const ORDER_TYPE_LABELS = {
  ot: "OwnersTable",
  non_ot: "Non-OwnersTable",
};

export const DEFAULT_SORT_BY = "name";
export const DEFAULT_SORT_ORDER = "asc";

const GRANDFATHERED =
  "An employee is grandfathered when, on the order's date, they had been on OwnersTable as that group's " +
  "employee without a full day's break since before the group's cutoff date. The day they were added to " +
  "OwnersTable counts, not their hire date.";

// Stated in this report's own terms: "subtotal" and "managed" don't mean what they mean in other reports.
export const NAME_COLUMN = {
  key: "name",
  label: "Group / Restaurant",
  show_info: true,
  description:
    "Each restaurant group, followed by its restaurants. A group's figures are the sum of its restaurants'. " +
    "A group without a cutoff date is not in the report, even when it is chosen in the filter.",
};

// Keys match the backend's amount keys, which are also its sort_by values.
export const AMOUNT_COLUMNS = [
  {
    key: "managed_subtotal",
    label: "Managed Subtotal",
    description:
      "The gross (amount plus discounts) of this restaurant's orders that have at least one managed perq, or one of the selected perqs when any are chosen. " +
      "A perq is managed here when it is set up as Managed at this restaurant, even after it is switched off. " +
      "A perq on a voided or deleted check or item doesn't make an order count, but once an order counts its gross " +
      "is the order's totals, voided and deleted checks included. Voided, deleted and excess-food orders are left " +
      "out. Industry card orders and orders without an employee count. Orders by employees grandfathered in this " +
      "restaurant's group are left out.",
  },
  {
    key: "managed_fee",
    label: "Managed Fee (1%)",
    description:
      "1% of this restaurant's Managed Subtotal, rounded to the cent. A group's fee is the sum of its " +
      "restaurants' fees, so it can differ by a few cents from 1% of the group's subtotal.",
  },
  {
    key: "industry_subtotal",
    label: "Industry Subtotal",
    description:
      "The same order gross, on this restaurant's orders paid with an Industry card. Orders by a " +
      "card holder grandfathered in the group that gave them the card are left out. An Industry card order " +
      "without a counted managed perq is not counted.",
  },
  {
    key: "industry_fee",
    label: "Industry Fee (5%)",
    description:
      "5% of this restaurant's Industry Subtotal, rounded to the cent, charged on top of the Managed Fee. " +
      "A group's fee is the sum of its restaurants' fees.",
  },
  {
    key: "forgone_managed_subtotal",
    label: "Forgone Managed Subtotal",
    forgone: true,
    description: `The amounts left out of the Managed Subtotal because the employee was grandfathered in this restaurant's group. ${GRANDFATHERED}`,
  },
  {
    key: "forgone_managed_fee",
    label: "Forgone Managed Fee (1%)",
    forgone: true,
    description: "1% of the Forgone Managed Subtotal: the fee waived for grandfathered employees. Shown for information, never billed.",
  },
  {
    key: "forgone_industry_subtotal",
    label: "Forgone Industry Subtotal",
    forgone: true,
    description: `The amounts left out of the Industry Subtotal because the card holder was grandfathered in the group that gave them the card. ${GRANDFATHERED}`,
  },
  {
    key: "forgone_industry_fee",
    label: "Forgone Industry Fee (5%)",
    forgone: true,
    description: "5% of the Forgone Industry Subtotal: the fee waived for grandfathered card holders. Shown for information, never billed.",
  },
  {
    key: "total_fee",
    label: "Total Fee",
    description: "The Managed Fee plus the Industry Fee: the amount owed for the period. Forgone amounts are not included.",
  },
];

// Subtotal columns that drill down to their orders, mapped to the endpoint's subtotal_type.
export const SUBTOTAL_TYPES = {
  managed_subtotal: "managed",
  industry_subtotal: "industry",
  forgone_managed_subtotal: "forgone_managed",
  forgone_industry_subtotal: "forgone_industry",
};

export const DEFAULT_ORDERS_SORT_BY = "opened";
export const DEFAULT_ORDERS_SORT_ORDER = "desc";

// A group drill-down keeps the restaurant filter so its orders match the group row's total.
export const getSubtotalOrdersApiParams = ({ isGroup, id, subtotalType }, apiFilters) => {
  const { restaurant_group_ids, restaurant_ids, ...filters } = apiFilters || {};
  const scope = isGroup ? { restaurant_group_id: id, restaurant_ids } : { restaurant_id: id };
  return { ...filters, ...scope, subtotal_type: subtotalType };
};

export const defaultBillingReportFilters = () => ({
  dateFilter: "Today",
  customDateFrom: dayjs(),
  customDateTo: dayjs(),
  restaurant_group_id: ALL_FILTER_VALUE,
  restaurant_ids: [ALL_FILTER_VALUE],
  order_type: ALL_ORDER_TYPE,
  applied_discount_names: [],
});

export const MISSING_DATE_RANGE_ERROR = "Select both from and through dates";
export const INVALID_DATE_RANGE_ERROR = "From date cannot be after through date";

// @returns {string|null} why Update is refused, mirroring the backend's required dates
export const getBillingReportFilterError = (filters) => {
  if (filters?.dateFilter === "Custom Date") {
    if (!isValidDayjsValue(filters.customDateFrom) || !isValidDayjsValue(filters.customDateTo)) {
      return MISSING_DATE_RANGE_ERROR;
    }
    if (!isValidCustomDateRange(filters.customDateFrom, filters.customDateTo)) {
      return INVALID_DATE_RANGE_ERROR;
    }
  }
  return null;
};

// resolveDateRange returns DD/MM/YYYY; the endpoint takes start_date/end_date as YYYY-MM-DD.
const toIsoDate = (value) => {
  const [day, month, year] = value.split("/");
  return `${year}-${month}-${day}`;
};

export const getBillingReportApiFilters = (filters) => {
  const params = {};

  const dateRange = resolveDateRange(filters);
  if (dateRange) {
    params.start_date = toIsoDate(dateRange.from);
    params.end_date = toIsoDate(dateRange.to);
  }

  if (filters?.restaurant_group_id && filters.restaurant_group_id !== ALL_FILTER_VALUE) {
    params.restaurant_group_ids = [filters.restaurant_group_id];
  }

  if (Array.isArray(filters?.restaurant_ids) && !filters.restaurant_ids.includes(ALL_FILTER_VALUE)) {
    params.restaurant_ids = filters.restaurant_ids;
  }

  if (filters?.order_type === "ot") params.has_ot_discount = true;
  if (filters?.order_type === "non_ot") params.has_ot_discount = false;

  // Omitted when none are selected, so the report bills every managed perq.
  if (filters?.applied_discount_names?.length) {
    params.applied_discount_names = filters.applied_discount_names;
  }

  return params;
};
